import { unzipSync } from "fflate"

// Client-side preview parser for B2B Stock files. Mirrors the server's
// `B2bStockFileParser.detect` (app/lib/b2b_stock_file_parser.rb) so the skip/replace
// warning shown before upload uses the SAME account_code / report_type / period that the
// server will store — keeping duplicate-detection accurate. KEEP IN LOCKSTEP with the
// server detector.
//
// .xlsx files are parsed in the browser (StockBaik=IDG, REG/SC=IDM Modern, Worksheet=wide
// MIDI/SAT/SIL). Legacy binary .xls (IDM `3.001.0`, some wide) can't be parsed reliably in
// the browser, so we derive what we can from the filename and let the server compute the
// authoritative metadata + row count at upload time ("Dihitung server").

export type StockDetected = {
  unknown?: false
  filename: string
  accountCode: string
  accountName: string
  reportType: string
  templateVersion: string
  periodYear: number
  periodMonth: number
  rowCount: number
}

export type StockUnknown = {
  unknown: true
  filename: string
  reason: string
}

export type StockResult = StockDetected | StockUnknown

const ACCOUNT_NAMES: Record<string, string> = {
  IDG: "Indogrosir",
  IDM: "Indomaret",
  MIDI: "PT MIDI UTAMA INDONESIA Tbk",
  SAT: "PT SUMBER ALFARIA TRIJAYA Tbk",
  SIL: "PT SUMBER INDAH LESTARI",
}

const MONTH_MAP: Record<string, number> = {
  jan: 1, january: 1, januari: 1,
  feb: 2, february: 2, februari: 2,
  mar: 3, march: 3, maret: 3,
  apr: 4, april: 4,
  may: 5, mei: 5,
  jun: 6, june: 6, juni: 6,
  jul: 7, july: 7, juli: 7,
  aug: 8, august: 8, agustus: 8, agu: 8, agt: 8, ags: 8,
  sep: 9, september: 9,
  oct: 10, october: 10, oktober: 10, okt: 10,
  nov: 11, november: 11,
  dec: 12, december: 12, desember: 12, des: 12,
}

// Mirror of B2bStockFileParser.parse_month_year
export function parseMonthYear(str: string): [number, number] | null {
  if (!str) return null
  const s = str.trim().toLowerCase()

  const match1 = s.match(/(jan|feb|mar|apr|may|mei|jun|jul|aug|agu|agt|ags|sep|oct|okt|nov|dec|des)[a-z]*[\s\-]*'*(20\d{2}|\d{2})/i)
  if (match1) {
    const m = MONTH_MAP[match1[1]]
    let y = parseInt(match1[2], 10)
    if (y < 100) y += 2000
    if (m && y > 0) return [y, m]
  }

  const match2 = s.match(/(\d{4})-(\d{2})/)
  if (match2) return [parseInt(match2[1], 10), parseInt(match2[2], 10)]

  return null
}

// Mirror of B2bStockFileParser.extract_date_from_string: a dd-mmm-yy(yy) date such as
// "01-Feb-2026". Returns [year, month, day].
function extractDateFromString(str: string): [number, number, number] | null {
  if (!str) return null
  const m = str.match(/(\d{1,2})[- /](jan|feb|mar|apr|may|mei|jun|jul|aug|agu|agt|ags|sep|oct|okt|nov|dec|des)[a-z]*[- /](\d{2,4})/i)
  if (!m) return null
  const day = parseInt(m[1], 10)
  const month = MONTH_MAP[m[2].toLowerCase()]
  let year = parseInt(m[3], 10)
  if (year < 100) year += 2000
  if (!month) return null
  return [year, month, day]
}

// The stock "date" in IDG/wide headers (e.g. "01-Feb-2026") is the snapshot date taken at
// the START of the next month, so the stock period is the PREVIOUS month. Mirror of the
// `prev = parsed_date - 1` logic in the server detector: returns a "YYYY-M" fallback string.
function previousMonthFallback(str: string): string | null {
  const d = extractDateFromString(str)
  if (!d) return null
  const date = new Date(Date.UTC(d[0], d[1] - 1, d[2]))
  date.setUTCDate(date.getUTCDate() - 1)
  return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`
}

// Mirror of B2bStockFileParser.resolve_period: filename first, then fallbacks in order.
export function resolvePeriod(
  filename: string,
  ...fallbacks: (string | null | undefined)[]
): [number, number] | null {
  const fromName = parseMonthYear(filename)
  if (fromName) return fromName
  for (const candidate of fallbacks) {
    const p = candidate ? parseMonthYear(candidate) : null
    if (p) return p
  }
  return null
}

export function detectAccountFromFilename(filename: string): { code: string; name: string } | null {
  const f = filename.toLowerCase()
  if (f.includes("idm") || f.includes("indomaret")) return { code: "IDM", name: ACCOUNT_NAMES.IDM }
  if (f.includes("idg") || f.includes("indogrosir")) return { code: "IDG", name: ACCOUNT_NAMES.IDG }
  if (f.includes("midi")) return { code: "MIDI", name: ACCOUNT_NAMES.MIDI }
  if (f.includes("sat") || f.includes("alfaria") || f.includes("alfamart")) return { code: "SAT", name: ACCOUNT_NAMES.SAT }
  if (f.includes("sil") || f.includes("sumber indah")) return { code: "SIL", name: ACCOUNT_NAMES.SIL }
  return null
}

// Mirror of B2bStockFileParser.report_type_from_filename_or_sheet
function reportTypeFromFilenameOrSheet(filename: string, sheetName?: string): "skincare" | "reguler" {
  if (sheetName?.toUpperCase() === "SC") return "skincare"
  const f = filename.toLowerCase()
  return f.includes("safi") || f.includes("skincare") || f.includes("beauty") ? "skincare" : "reguler"
}

export async function parseB2bStockForPreview(file: File): Promise<StockResult> {
  const filename = file.name
  const lower = filename.toLowerCase()

  // Legacy binary .xls: derive account + period from the filename only; the server reads
  // the actual sheet (3.001.0 vs Worksheet) and computes the authoritative row count.
  if (lower.endsWith(".xls")) {
    const period = parseMonthYear(filename)
    const acc = detectAccountFromFilename(filename)
    if (!period || !acc) {
      return {
        unknown: true,
        filename,
        reason: 'Gagal mendeteksi info dari nama file .xls. Pastikan nama file memuat akun dan periode (mis. "STOCK IDM DES 23.xls"). Detail akan dihitung server saat upload.',
      }
    }
    return {
      filename,
      accountCode: acc.code,
      accountName: acc.name,
      reportType: reportTypeFromFilenameOrSheet(filename),
      templateVersion: acc.code === "IDM" ? "idm_legacy_stock_v1" : "wide_stock_xls_v1",
      periodYear: period[0],
      periodMonth: period[1],
      rowCount: 0, // Cannot parse binary .xls client-side; server computes it.
    }
  }

  if (!lower.endsWith(".xlsx")) {
    return { unknown: true, filename, reason: "Format tidak didukung. Hanya .xls dan .xlsx." }
  }

  try {
    const buf = await file.arrayBuffer()
    const data = new Uint8Array(buf)
    const decode = (b: Uint8Array) => new TextDecoder().decode(b)

    const meta = unzipSync(data, {
      filter: (f) => f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
    })
    if (!meta["xl/workbook.xml"]) {
      return { unknown: true, filename, reason: "workbook.xml tidak ditemukan dalam file." }
    }

    const wbXml = decode(meta["xl/workbook.xml"])
    const sheetEntries = parseSheetEntries(wbXml)
    const sheetNames = sheetEntries.map((e) => e.name)
    const relsXml = meta["xl/_rels/workbook.xml.rels"] ? decode(meta["xl/_rels/workbook.xml.rels"]) : ""

    if (sheetNames.includes("StockBaik")) {
      return detectIdgXlsx(data, decode, filename, sheetEntries, relsXml)
    }
    if (sheetNames.includes("REG") || sheetNames.includes("SC")) {
      return detectIdmModernXlsx(data, decode, filename, sheetNames, sheetEntries, relsXml)
    }
    if (sheetNames.includes("Worksheet")) {
      return detectWideXlsx(data, decode, filename, sheetEntries, relsXml)
    }

    return {
      unknown: true,
      filename,
      reason: `Template tidak dikenal. Sheet ditemukan: ${sheetNames.join(", ") || "(tidak ada)"}`,
    }
  } catch (err) {
    return { unknown: true, filename, reason: err instanceof Error ? err.message : "Gagal membaca file" }
  }
}

// ---------------------------------------------------------------------------
// Template detectors (mirror B2bStockFileParser.detect_xlsx)
// ---------------------------------------------------------------------------

function detectIdgXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): StockResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "StockBaik")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet StockBaik tidak ditemukan." }

  const extracted = unzipSync(data, { filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row3 = getRowXml(rowParts, 3)
  const row3Val = rowCellValues(row3, ss).compact().map((v) => v.toString()).join(" ")

  const period = resolvePeriod(filename, previousMonthFallback(row3Val))
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak ditemukan dalam file IDG: "${row3Val}".` }
  }

  // Map header row 5 to find the BRANCH + QTY columns (server falls back to C / D).
  const row5 = getRowXml(rowParts, 5)
  let branchCol = "C"
  let qtyCol = "D"
  for (const cellXml of row5.split("<c ").slice(1)) {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    if (!colLetter) continue
    const v = cellValueForColumn(row5, colLetter, ss)?.toString().trim().toUpperCase() ?? ""
    if (v.includes("BRANCH") || v.includes("CABANG")) branchCol = colLetter
    else if (v.includes("QTY") || v.includes("QUANTITY")) qtyCol = colLetter
  }

  // Estimate: each subrow that carries a branch + non-zero qty is one record.
  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const rowNum = parseInt(rowParts[i].match(/\br="(\d+)"/)?.[1] ?? "0", 10)
    if (rowNum < 6) continue
    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue
    const branch = cellValueForColumn(rowXml, branchCol, ss)?.toString().trim()
    const qty = Number(cellValueForColumn(rowXml, qtyCol, ss))
    if (branch && branch.toUpperCase() !== "TOTAL" && qty) rowCount++
  }

  return {
    filename,
    accountCode: "IDG",
    accountName: ACCOUNT_NAMES.IDG,
    reportType: reportTypeFromFilenameOrSheet(filename, "StockBaik"),
    templateVersion: "idg_stock_v1",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

function detectIdmModernXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetNames: string[],
  sheetEntries: SheetEntry[],
  relsXml: string
): StockResult {
  const sheetName = sheetNames.includes("SC") ? "SC" : "REG"
  const wsPath = resolveSheetPath(sheetEntries, relsXml, sheetName)
  if (!wsPath) return { unknown: true, filename, reason: `Sheet ${sheetName} tidak ditemukan.` }

  const extracted = unzipSync(data, { filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  // Period: filename first, else the YYYY-MM in cell A2.
  const row2 = getRowXml(rowParts, 2)
  const a2 = cellValueForColumn(row2, "A", ss)?.toString().trim() ?? ""
  const period = resolvePeriod(filename, a2)
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak ditemukan untuk file IDM Modern: "${a2}".` }
  }

  // Locate the PRODUCT column from header row 1 (server falls back to E).
  const row1 = getRowXml(rowParts, 1)
  let productCol = "E"
  for (const cellXml of row1.split("<c ").slice(1)) {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    if (!colLetter) continue
    const v = cellValueForColumn(row1, colLetter, ss)?.toString().trim().toUpperCase() ?? ""
    if (v === "PRODUCT" || v === "ITEM") productCol = colLetter
  }

  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const rowNum = parseInt(rowParts[i].match(/\br="(\d+)"/)?.[1] ?? "0", 10)
    if (rowNum < 2) continue
    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue
    const product = cellValueForColumn(rowXml, productCol, ss)?.toString().trim()
    if (product && product.toUpperCase() !== "TOTAL") rowCount++
  }

  return {
    filename,
    accountCode: "IDM",
    accountName: ACCOUNT_NAMES.IDM,
    reportType: reportTypeFromFilenameOrSheet(filename, sheetName),
    templateVersion: "idm_modern_stock_v1",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

function detectWideXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): StockResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "Worksheet")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet Worksheet tidak ditemukan." }

  const extracted = unzipSync(data, { filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row1Val = rowCellValues(getRowXml(rowParts, 1), ss).compact().map((v) => v.toString()).join(" ")
  const row2Val = rowCellValues(getRowXml(rowParts, 2), ss).compact().map((v) => v.toString()).join(" ")
  const row3Val = rowCellValues(getRowXml(rowParts, 3), ss).compact().map((v) => v.toString()).join(" ")
  const row4A = cellValueForColumn(getRowXml(rowParts, 4), "A", ss)?.toString().trim() ?? ""

  const companyTitle = `${row1Val} ${row2Val} ${row3Val}`
  let accountCode = ""
  if (companyTitle.includes("PT SUMBER ALFARIA TRIJAYA")) accountCode = "SAT"
  else if (companyTitle.includes("PT SUMBER INDAH LESTARI")) accountCode = "SIL"
  else if (companyTitle.includes("PT MIDI UTAMA INDONESIA")) accountCode = "MIDI"
  else {
    const acc = detectAccountFromFilename(filename)
    if (acc) accountCode = acc.code
    else return { unknown: true, filename, reason: `Judul perusahaan tidak dikenal: "${companyTitle.trim()}".` }
  }

  const fallback = previousMonthFallback(row1Val) ?? previousMonthFallback(row4A)
  const period = resolvePeriod(filename, fallback)
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak dapat ditemukan dari file "${filename}".` }
  }

  // Estimate: count product rows (row 8+, col A index > 0, col B PLU present).
  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const rowNum = parseInt(rowParts[i].match(/\br="(\d+)"/)?.[1] ?? "0", 10)
    if (rowNum < 8) continue
    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue
    const idx = Number(cellValueForColumn(rowXml, "A", ss))
    const plu = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (idx > 0 && plu && plu.toUpperCase() !== "TOTAL") rowCount++
  }

  return {
    filename,
    accountCode,
    accountName: ACCOUNT_NAMES[accountCode],
    reportType: reportTypeFromFilenameOrSheet(filename),
    templateVersion: "wide_stock_xlsx_v1",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

// ---------------------------------------------------------------------------
// Low-level .xlsx helpers (same streaming approach as the server / sibling parsers)
// ---------------------------------------------------------------------------

type SheetEntry = { name: string; rId: string }

function parseSheetEntries(wbXml: string): SheetEntry[] {
  const entries: SheetEntry[] = []
  const re = /<sheet\b[^>]*name="([^"]*)"[^>]*r:id="([^"]*)"[^>]*>/g
  let m
  while ((m = re.exec(wbXml)) !== null) entries.push({ name: m[1], rId: m[2] })
  return entries
}

function resolveSheetPath(entries: SheetEntry[], relsXml: string, sheetName: string): string | null {
  const entry = entries.find((e) => e.name === sheetName)
  if (!entry) return null
  const relParts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < relParts.length; i++) {
    if (relParts[i].includes(`Id="${entry.rId}"`)) {
      const targetMatch = relParts[i].match(/\bTarget="([^"]+)"/)
      if (targetMatch) {
        const target = targetMatch[1]
        if (target.startsWith("../")) return target.slice(3)
        if (target.startsWith("/")) return target.slice(1)
        return `xl/${target}`
      }
    }
  }
  return null
}

function parseSharedStrings(bytes: Uint8Array | undefined, decode: (b: Uint8Array) => string): string[] {
  if (!bytes) return []
  const xml = decode(bytes)
  const ss: string[] = []
  for (const part of xml.split("<si>").slice(1)) {
    const matches = [...part.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    ss.push(matches.map((m) => m[1]).join(""))
  }
  return ss
}

function getRowXml(rowParts: string[], rowNumber: number): string {
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    if (attr && parseInt(attr[1], 10) === rowNumber) {
      const end = rowParts[i].indexOf("</row>")
      return end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    }
  }
  return ""
}

function findCellXml(rowXml: string, colLetter: string): string {
  for (const part of rowXml.split("<c ").slice(1)) {
    if (new RegExp(`r="${colLetter}\\d+"`).test(part)) return "<c " + part
  }
  return ""
}

function cellValueForColumn(rowXml: string, colLetter: string, ss: string[]): string | number | null {
  const cellXml = findCellXml(rowXml, colLetter)
  if (!cellXml) return null

  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? unescapeXml(matches.map((m) => m[1]).join("")) : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? unescapeXml(m[1]) : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  if (m) {
    const rawVal = unescapeXml(m[1])
    const num = parseFloat(rawVal)
    return isNaN(num) ? rawVal : num
  }
  return null
}

function rowCellValues(rowXml: string, ss: string[]): (string | number | null)[] {
  const cells = rowXml.split("<c ").slice(1)
  return cells.map((cellXml) => {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    return colLetter ? cellValueForColumn(rowXml, colLetter, ss) : null
  })
}

function unescapeXml(str: string): string {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
}

declare global {
  interface Array<T> {
    compact(): Exclude<T, null | undefined>[]
  }
}

if (!Array.prototype.compact) {
  Array.prototype.compact = function () {
    return this.filter((v) => v !== null && v !== undefined)
  }
}
